<template>
  <b-modal
    v-model="modal"
    :id="modalId"
    :size="size"
    :title="null"
    :hide-header="true"
    :hide-footer="true"
    :centered="centered"
    :no-close-on-backdrop="noCloseOnBackdrop"
    :no-close-on-esc="noCloseOnEsc"
    body-class="p-0"
    modal-class="mc-message-dialog"
    @cancel="handleCancel"
  >
    <div class="mc-message-dialog__wrapper" :style="wrapperStyle">
      <div class="mc-message-dialog__header">
        <img v-if="!hideIcon" class="mc-message-dialog__icon" :src="iconSrc" :alt="variant" />
        <h2 v-if="title" class="mc-message-dialog__title">{{ title }}</h2>
      </div>

      <div class="mc-message-dialog__content-wrapper" :style="messagesStyle">
        <div class="mc-message-dialog__content">
          <div v-if="subtitle" class="mc-message-dialog__subtitle">{{ subtitle }}</div>
          <div class="mc-message-dialog__divider"></div>
          <div class="mc-message-dialog__messages">
            <div
              v-for="(item, index) in content"
              :key="index"
              class="mc-message-dialog__message-item"
            >
              <template v-if="isNumbered">
                <span class="mc-message-dialog__number">{{ index + 1 }}.</span>
                <span v-if="useHtml" class="mc-message-dialog__text" v-html="item"></span>
                <span v-else class="mc-message-dialog__text">{{ item }}</span>
              </template>
              <span
                v-if="useHtml && !isNumbered"
                class="mc-message-dialog__text"
                v-html="item"
              ></span>
              <span v-else-if="!isNumbered" class="mc-message-dialog__text">{{ item }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showPrimaryButton || showSecondaryButton" class="mc-message-dialog__footer">
        <McButton
          v-if="showSecondaryButton"
          :text="secondaryButton.text"
          variant="secondary"
          block
          :loading="secondaryButton.loading"
          :disabled="secondaryButton.disabled"
          @click="handleSecondaryClick"
        />
        <McButton
          v-if="showPrimaryButton"
          :text="primaryButton.text"
          variant="primary"
          block
          :loading="primaryButton.loading"
          :disabled="primaryButton.disabled"
          @click="handlePrimaryClick"
        />
      </div>
    </div>
  </b-modal>
</template>

<script>
import McButton from '@/components/McButton'

export default {
  name: 'McMessageDialog',
  components: {
    McButton,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    content: {
      type: Array,
      default: () => [],
    },
    variant: {
      type: String,
      default: 'error',
      validator: (val) => ['error', 'success', 'warning', 'info'].includes(val),
    },
    size: {
      type: String,
      default: 'md',
    },
    centered: {
      type: Boolean,
      default: true,
    },
    hideIcon: {
      type: Boolean,
      default: false,
    },
    noCloseOnBackdrop: {
      type: Boolean,
      default: true,
    },
    noCloseOnEsc: {
      type: Boolean,
      default: true,
    },
    primaryButton: {
      type: Object,
      default: () => ({
        text: 'Tutup',
        visible: true,
        loading: false,
        disabled: false,
      }),
    },
    secondaryButton: {
      type: Object,
      default: () => ({
        text: 'Batal',
        visible: false,
        loading: false,
        disabled: false,
      }),
    },
    modalHeight: {
      type: [String, Number],
      default: 540,
    },
    useHtml: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:value', 'primary-click', 'secondary-click', 'cancel'],
  data() {
    return {
      modalId: `mc-message-dialog-${this._uid}`,
    }
  },
  computed: {
    modal: {
      set(val) {
        this.$emit('update:value', val)
      },
      get() {
        return this.value
      },
    },
    isNumbered() {
      return Array.isArray(this.content) && this.content.length > 1
    },
    iconSrc() {
      const iconMap = {
        error: require('@/assets/images/alert-report-danger.png'),
        success: require('@/assets/images/success-icon.png'),
        warning: require('@/assets/images/warning-icon.png'),
        info: '',
      }
      return iconMap[this.variant] || iconMap.error
    },
    showPrimaryButton() {
      return this.primaryButton && this.primaryButton.visible !== false
    },
    showSecondaryButton() {
      return this.secondaryButton && this.secondaryButton.visible === true
    },
    wrapperStyle() {
      const height =
        typeof this.modalHeight === 'number' ? `${this.modalHeight}px` : this.modalHeight
      return {
        minHeight: height,
        maxHeight: '90vh',
      }
    },
    messagesStyle() {
      return {
        flex: 1,
        minHeight: '0',
      }
    },
  },
  methods: {
    handlePrimaryClick() {
      this.$emit('primary-click')
      if (this.primaryButton && typeof this.primaryButton.action === 'function') {
        this.primaryButton.action()
      }
    },
    handleSecondaryClick() {
      this.$emit('secondary-click')
      if (this.secondaryButton && typeof this.secondaryButton.action === 'function') {
        this.secondaryButton.action()
      }
    },
    handleCancel() {
      this.modal = false
      this.$emit('cancel')
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-message-dialog {
  ::v-deep .modal-dialog {
    width: 520px;
    max-width: calc(100vw - 1rem);
  }

  ::v-deep .modal-content {
    border-radius: 12px;
    border: none;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  }

  ::v-deep .modal-body {
    padding: 0;
    overflow: visible;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
  }

  &__header {
    padding: 0px 32px 24px 32px;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
  }

  &__icon {
    width: 180px;
    height: 180px;
    object-fit: contain;
    margin-bottom: 12px;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: #1a1a1a;
    text-align: center;
    margin: 0;
    line-height: 1.3;
  }

  &__content-wrapper {
    padding: 0 32px;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  &__content {
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e7ebee;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  &__subtitle {
    padding: 14px 20px 0;
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
    flex-shrink: 0;
  }

  &__divider {
    margin: 10px 20px 0;
    height: 1px;
    background-color: #dee2e6;
    flex-shrink: 0;
  }

  &__messages {
    padding: 12px 20px 20px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #adb5bd;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background-color: #6c757d;
    }
  }

  &__message-item {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    line-height: 1.6;
  }

  &__number {
    font-size: 15px;
    font-weight: 500;
    color: #495057;
    flex-shrink: 0;
  }

  &__text {
    font-size: 14px;
    color: #495057;
    word-break: break-word;
  }

  &__footer {
    padding: 16px 32px 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex-shrink: 0;
  }
}
</style>
